import {
	CreateDrawerFormProps,
	FormDrawerContentProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nTextField,
	MenuItemVisibilityField,
	SingleSelectControl,
	UpdateDrawerFormProps,
	defaultVisibilityOptions,
	useDynamicConfig,
	validateEachLanguage,
} from "@dgs/core";
import { FormikErrors, useFormikContext } from "formik";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { bottomNavBarItemService } from "../shared/api/bottomNavBarService";
import {
	BottomNavBarItemBaseTranslationEntry,
	BottomNavBarItemFormValues,
	BottomNavBarItemType,
	BottomNavBarWebViewItemTranslationEntry,
} from "../shared/types/bottomNavBarItem";

const types: BottomNavBarItemType[] = ["events", "notifications", "userProfile", "webView", "login"];
const visibility = [...defaultVisibilityOptions];

const useValidate = () => {
	const { t } = useTranslation();

	return (values: BottomNavBarItemFormValues) => {
		const errors: FormikErrors<BottomNavBarItemFormValues> = {};
		if (!values.type) {
			errors.type = t("Field is required");
		}

		if (!values.visibility) {
			errors.visibility = t("Field is required");
		}

		return {
			...errors,
			...validateEachLanguage<BottomNavBarItemFormValues["translations"][number]>(
				values.translations,
				(_, translation) => {
					const errors: Record<string, any> = {};
					if (!translation.name) {
						errors.name = t("Field is required");
					}
					if (values.type === "webView" && !("data" in translation && translation.data.url)) {
						errors.data = { url: t("Field is required") };
					}
					return errors;
				},
			),
		};
	};
};

const typeToEmptyTranslation: {
	[K in BottomNavBarItemType]: BottomNavBarItemBaseTranslationEntry | BottomNavBarWebViewItemTranslationEntry;
} = {
	notifications: { name: "" },
	events: { name: "" },
	userProfile: { name: "" },
	login: { name: "" },
	webView: { name: "", data: { url: "" } },
};

const BottomNavBarItemDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();
	const { values, setValues } = useFormikContext<BottomNavBarItemFormValues>();
	const [visibilityDisabled, setVisibilityDisabled] = useState(values.type === "login");

	const handleChangeType = useCallback(
		(type: BottomNavBarItemType | null) => {
			if (type === "login") {
				setVisibilityDisabled(true);
			} else {
				setVisibilityDisabled(false);
			}

			if (type === null) {
				return;
			}

			void setValues((previous) => ({
				...previous,
				type,
				visibility: type === "login" ? "public" : previous.visibility,
				translations: previous.translations.map((previousTranslation) => ({
					...typeToEmptyTranslation[type],
					...previousTranslation,
				})),
			}));
		},
		[setValues],
	);

	return (
		<I18nFormDrawerContent {...props} emptyTranslation={typeToEmptyTranslation[values.type]}>
			<I18nTextField label={t("Label")} name="name" required="onlyOnMainForm" />
			<I18nOnlyOnMainForm>
				<SingleSelectControl
					required
					clearable={false}
					label={t("Type")}
					name="type"
					options={types}
					getLabel={(type) => t(`bottom_nav_bar_item_type_${type}`)}
					getValue={(value) => value}
					onChange={(e) => handleChangeType(e.target.value)}
					value={values.type}
				/>
				<MenuItemVisibilityField
					label={t("Visibility")}
					name="visibility"
					options={visibility}
					disabled={visibilityDisabled}
					required
				/>
			</I18nOnlyOnMainForm>
			{values.type === "webView" && <I18nTextField label={t("URL")} name="data.url" required />}
		</I18nFormDrawerContent>
	);
};

export const BottomNavBarItemCreateDrawer: FC<CreateDrawerFormProps<BottomNavBarItemFormValues>> = (drawerProps) => {
	const { t } = useTranslation();
	const { languageSettings } = useDynamicConfig();
	const validate = useValidate();

	const initialValue: BottomNavBarItemFormValues = useMemo(() => {
		return {
			visibility: "all",
			type: "events",
			translations: [{ locale: languageSettings.fallback, ...typeToEmptyTranslation["events"] }],
		};
	}, [languageSettings.fallback]);

	return (
		<I18nFormDrawer
			{...drawerProps}
			validate={validate}
			title={t("Create nav bar item")}
			initialValues={initialValue}
			renderForm={(props) => <BottomNavBarItemDrawerContent {...props} />}
		/>
	);
};

export const BottomNavBarItemUpdateDrawer: FC<UpdateDrawerFormProps<BottomNavBarItemFormValues>> = (drawerProps) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<I18nFormFetchDrawer
			{...drawerProps}
			validate={validate}
			queryFn={() => bottomNavBarItemService.show(drawerProps.id).then(({ data }) => data.data)}
			queryKey={bottomNavBarItemService.showKey(drawerProps.id)}
			title={t("Edit nav bar item")}
			renderSuccess={(props) => <BottomNavBarItemDrawerContent {...props} />}
		/>
	);
};
